import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Modal,
  FlatList,
  Text,
  TextInput,
  ActivityIndicator,
  Alert,
  Animated,
} from "react-native";
import { useChatList } from "../hooks/useChatList";
import { ChatList } from "../components/chat/ChatList";
import { ChatListHeader } from "../components/chat/ChatListHeader";
import { LoadingState } from "../components/ui/LoadingState";
import { ErrorState } from "../components/ui/ErrorState";
import { EmptyState } from "../components/ui/EmptyState";
import { Feather } from "@expo/vector-icons";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { findOrCreateChat } from "../utils/chatUtils";

export function ChatListScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const { data: chats, isLoading, error } = useChatList();
  const navigation = useNavigation();

  // Animations - start at 1 to prevent dimming issues during hot reload
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const slideAnim = useRef(new Animated.Value(0)).current;

  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const queryClient = useQueryClient();
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { user } = useAuth();

  // Refresh read status when screen comes into focus
  // This ensures "New" pills are updated after viewing a chat
  useFocusEffect(
    useCallback(() => {
      if (user?.id) {
        // Invalidate the read status query to ensure fresh data
        queryClient.invalidateQueries({ queryKey: ['chat-read-status', user.id] });
      }
    }, [user?.id, queryClient])
  );

  const { data: vendors, isLoading: isLoadingVendors } = useQuery({
    queryKey: ["vendors", searchQuery],
    queryFn: async () => {
      // Only fetch vendors if there's a search query
      if (!searchQuery) return [];

      const query = supabase.from("vendors").select("*").order("store_name");

      // Apply search filter
      query.ilike("store_name", `%${searchQuery}%`);

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!searchQuery, // Only run the query when search query exists
  });

  const startChat = async (vendorId: string, vendorUserId: string) => {
    try {
      setIsCreatingChat(true);
      const {
        data: { user: currentUser },
      } = await supabase.auth.getUser();
      if (!currentUser) throw new Error("Not authenticated");

      console.log(
        "Starting chat with vendor ID:",
        vendorId,
        "vendor user ID:",
        vendorUserId,
        "current user ID:",
        currentUser.id
      );

      // Find or create chat using user IDs (not vendor_id) to ensure consistent chat grouping
      const chatId = await findOrCreateChat(currentUser.id, vendorUserId, vendorId);

      console.log("Navigating to chat:", chatId);

      // Invalidate the chat list query to refresh the list
      await queryClient.invalidateQueries({ queryKey: ["chats"] });

      setSearchModalVisible(false);
      navigation.navigate("Chat", { id: chatId });
    } catch (error) {
      console.error("Error starting chat:", error);
      Alert.alert("Error", "Failed to start chat. Please try again.");
    } finally {
      setIsCreatingChat(false);
    }
  };

  const renderVendorItem = ({ item }) => (
    <TouchableOpacity
      style={styles.vendorItem}
      onPress={() => startChat(item.id, item.user_id)}
      disabled={isCreatingChat}
    >
      <View style={styles.vendorInfo}>
        <Text style={styles.vendorName}>{item.store_name}</Text>
        <Text style={styles.vendorLocation}>{item.borough}</Text>
      </View>
      {isCreatingChat ? (
        <ActivityIndicator size="small" color={colors.primary} />
      ) : (
        <Feather name="message-circle" size={20} color={colors.primary} />
      )}
    </TouchableOpacity>
  );

  // Handle refresh
  const handleRefresh = async () => {
    if (!user) return;

    setIsRefreshing(true);
    try {
      // Invalidate the chat list query to force refresh
      await queryClient.invalidateQueries({ queryKey: ["chats", user.id] });
    } catch (error) {
      console.error("Error refreshing chat list:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.container,
          {
            opacity: fadeAnim,
            transform: [{ translateY: slideAnim }],
          },
        ]}
      >
        <View style={styles.header}>
          <View style={styles.headerTop}>
            <View>
              <Text style={styles.welcomeGreeting}>Messages</Text>
              <Text style={styles.welcomeSubtext}>Your conversations with users</Text>
            </View>
            <TouchableOpacity 
              style={styles.archiveButton}
              onPress={() => navigation.navigate('ArchivedChats')}
            >
              <Feather name="archive" size={22} color={colors.textLight} />
            </TouchableOpacity>
          </View>
        </View>

        {!chats || chats.length === 0 ? (
          <EmptyState
            icon="message-circle"
            title="No messages yet"
            description="Start a conversation with a seller"
          />
        ) : (
          <ChatList
            chats={chats}
            onRefresh={handleRefresh}
            isRefreshing={isRefreshing}
          />
        )}
      </Animated.View>

      {/* <TouchableOpacity 
        style={styles.fab}
        onPress={() => setSearchModalVisible(true)}
      >
        <Feather name="message-square" size={24} color={colors.surface} />
      </TouchableOpacity> */}

      <Modal
        visible={searchModalVisible}
        animationType="slide"
        transparent={true}
        onRequestClose={() => setSearchModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>New Message</Text>
              <TouchableOpacity
                onPress={() => setSearchModalVisible(false)}
                style={styles.closeButton}
              >
                <Feather name="x" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>

            <View style={styles.searchContainer}>
              <Feather name="search" size={20} color={colors.textLight} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search for a vendor..."
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoFocus
              />
            </View>

            {isLoadingVendors ? (
              <LoadingState />
            ) : (
              <FlatList
                data={searchQuery ? vendors : []}
                renderItem={renderVendorItem}
                keyExtractor={(item) => item.id}
                contentContainerStyle={styles.vendorList}
                ListEmptyComponent={() => (
                  <EmptyState
                    icon="search"
                    title={
                      searchQuery ? "No vendors found" : "Search for vendors"
                    }
                    description={
                      searchQuery
                        ? "Try a different search term"
                        : "Type in the search box to find vendors to message"
                    }
                  />
                )}
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.md,
      paddingBottom: spacing.sm,
      backgroundColor: colors.background,
    },
    headerTop: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "flex-start",
    },
    archiveButton: {
      padding: spacing.sm,
      marginTop: spacing.xs,
    },
    welcomeGreeting: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    welcomeSubtext: {
      fontSize: 16,
      color: colors.textSecondary,
      marginTop: 2,
      fontWeight: "500",
    },
    fab: {
      position: "absolute",
      right: spacing.lg,
      bottom: spacing.lg,
      width: 56,
      height: 56,
      borderRadius: 28,
      backgroundColor: colors.primary,
      alignItems: "center",
      justifyContent: "center",
      elevation: 4,
      shadowColor: colors.shadow,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 4,
    },
    modalContainer: {
      flex: 1,
      backgroundColor: "rgba(0,0,0,0.5)",
    },
    modalContent: {
      flex: 1,
      backgroundColor: colors.background,
      marginTop: 50,
      borderTopLeftRadius: 20,
      borderTopRightRadius: 20,
    },
    modalHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    modalTitle: {
      ...typography.subtitle,
      fontSize: 18,
    },
    closeButton: {
      padding: spacing.sm,
    },
    searchContainer: {
      flexDirection: "row",
      alignItems: "center",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      gap: spacing.sm,
    },
    searchInput: {
      flex: 1,
      fontSize: 16,
      color: colors.text,
    },
    vendorList: {
      padding: spacing.md,
    },
    vendorItem: {
      flexDirection: "row",
      alignItems: "center",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    vendorInfo: {
      flex: 1,
    },
    vendorName: {
      ...typography.subtitle,
      marginBottom: spacing.xs,
    },
    vendorLocation: {
      ...typography.caption,
      color: colors.textLight,
    },
  });
